import { distinctUntilChanged, filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';

import { Injectable, QueryList } from '@angular/core';

import { shareRef } from '../../operators/share-ref.operator';
import { fromQuery } from '../../operators/from-query.operator';
import { lastValue } from '../../operators/last-value.operator';
import { StepWizardStepDirective } from './directives/step-wizard-step.directive';

@Injectable()
export class StepWizardService {
  private stepsQuery$ = new ReplaySubject<QueryList<StepWizardStepDirective>>(1);

  private setIdx$ = new BehaviorSubject(0);

  readonly close$ = new Subject<boolean>();

  readonly steps$: Observable<StepWizardStepDirective[]> = this.stepsQuery$.pipe(switchMap(fromQuery), shareRef());

  readonly stepCount$: Observable<number> = this.steps$.pipe(
    map((steps) => steps.length),
    shareRef(),
  );

  readonly activeIdx$: Observable<number> = combineLatest([this.setIdx$, this.stepCount$]).pipe(
    map(([idx, steps]) => Math.min(steps - 1, Math.max(0, idx))),
    shareRef(),
  );

  readonly activeStep$: Observable<StepWizardStepDirective> = combineLatest([this.activeIdx$, this.steps$]).pipe(
    map(([idx, steps]) => steps[idx]),
    distinctUntilChanged(),
    filter((step) => !!step),
    shareRef(),
  );

  readonly isFirstStep$: Observable<boolean> = this.activeIdx$.pipe(
    map((idx) => !idx),
    shareRef(),
  );

  readonly isLastStep$: Observable<boolean> = combineLatest([this.stepCount$, this.activeIdx$]).pipe(
    map(([steps, idx]) => steps - 1 <= idx),
    shareRef(),
  );

  readonly asserting$ = new BehaviorSubject(false);

  setActiveIdx(idx: number): void {
    this.setIdx$.next(idx);
  }

  setStepsQuery(query: QueryList<StepWizardStepDirective>): void {
    this.stepsQuery$.next(query);
  }

  stepBack(): void {
    this.moveStep(-1);
  }

  stepForward(): void {
    this.assertCanContinue().subscribe(() => this.moveStep(1));
  }

  saveAndClose(): void {
    this.assertCanContinue().subscribe(() => this.close$.next(true));
  }

  private assertCanContinue(): Observable<boolean> {
    this.asserting$.next(true);

    return this.activeStep$.pipe(
      take(1),
      switchMap((step) => (step.canContinue === false ? of(false) : step.assertContinue?.() || of(true))),
      finalize(() => this.asserting$.next(false)),
      filter((canContinue) => canContinue),
    );
  }

  private moveStep(move: number): void {
    this.setActiveIdx(lastValue(this.activeIdx$, 0) + move);
  }
}
